import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Detects whether the app is already running as an installed PWA
 * (iOS standalone or Android/desktop display-mode standalone).
 */
const isStandalone = () => {
  if (typeof window === "undefined") return false;
  // iOS Safari
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  // Everything else
  const matchStandalone = window.matchMedia?.("(display-mode: standalone)").matches;
  return Boolean(iosStandalone || matchStandalone);
};

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

const DISMISS_KEY = "smplify:install-prompt-dismissed-v1";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/**
 * Install prompt banner.
 *
 * - On Android / desktop Chromium: surfaces the native `beforeinstallprompt`
 *   event behind a friendly "Install" CTA.
 * - On iOS: shows a one-time instructional banner (Share → Add to Home Screen)
 *   because iOS does not expose a programmatic install API.
 * - Hidden entirely when already running standalone or the user dismissed it
 *   (persisted in localStorage).
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS has no beforeinstallprompt — show the manual hint instead.
    if (isIos()) {
      setIos(true);
      setShow(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      role="region"
      aria-label="Install Smplify"
      className="fixed inset-x-2 z-[60] rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur-sm md:hidden"
      style={{
        bottom: `calc(env(safe-area-inset-bottom) + 4.5rem)`,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 text-sm">
          <p className="font-medium text-foreground">Install Smplify</p>
          {ios ? (
            <p className="mt-0.5 text-muted-foreground">
              Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" /> then{" "}
              <span className="font-medium">Add to Home Screen</span>
              <Plus className="ml-0.5 inline h-3.5 w-3.5 align-text-bottom" />
            </p>
          ) : (
            <p className="mt-0.5 text-muted-foreground">
              Get app-like access from your home screen.
            </p>
          )}
        </div>
        {!ios && (
          <Button size="sm" onClick={install} className="shrink-0">
            Install
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="h-8 w-8 shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default InstallPrompt;
